import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calculator } from "lucide-react";
import { toast } from "sonner";

export default function MagneticForceCalculator() {
  const [charge, setCharge] = useState("");
  const [velocity, setVelocity] = useState("");
  const [field, setField] = useState("");
  const [angle, setAngle] = useState("90");
  const [result, setResult] = useState<number | null>(null);

  const calculateForce = () => {
    const q = parseFloat(charge);
    const v = parseFloat(velocity);
    const b = parseFloat(field);
    const theta = parseFloat(angle) * (Math.PI / 180);

    if (isNaN(q) || isNaN(v) || isNaN(b) || isNaN(theta)) {
      toast.error("Please enter valid values");
      return;
    }

    const force = Math.abs(q) * v * b * Math.sin(theta);
    setResult(force);
    toast.success("Calculation complete!");
  };

  const reset = () => {
    setCharge("");
    setVelocity("");
    setField("");
    setAngle("90");
    setResult(null);
  };

  return (
    <PageLayout calculatorName="Magnetic Force Calculator" calculatorPath="/calculator/magnetic-force">
      <div className="max-w-2xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">Magnetic Force Calculator</h1>
          <p className="text-muted-foreground">
            Calculate force on a moving charge in a magnetic field
          </p>
        </div>

        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>Formula: F = qvBsinθ</CardTitle>
            <CardDescription>
              Where q is charge (C), v is velocity (m/s), B is field (T), θ is angle
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="charge">Charge (C)</Label>
                <Input
                  id="charge"
                  type="number"
                  step="any"
                  placeholder="Enter charge (e.g., 1.6e-19)"
                  value={charge}
                  onChange={(e) => setCharge(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">Electron: -1.6×10⁻¹⁹ C</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="velocity">Velocity (m/s)</Label>
                <Input
                  id="velocity"
                  type="number"
                  step="any"
                  placeholder="Enter velocity"
                  value={velocity}
                  onChange={(e) => setVelocity(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="field">Magnetic Field (T)</Label>
                <Input
                  id="field"
                  type="number"
                  step="any"
                  placeholder="Enter magnetic field strength"
                  value={field}
                  onChange={(e) => setField(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="angle">Angle (degrees)</Label>
                <Input
                  id="angle"
                  type="number"
                  step="0.1"
                  placeholder="Angle between v and B"
                  value={angle}
                  onChange={(e) => setAngle(e.target.value)}
                />
              </div>
            </div>

            <div className="flex gap-3">
              <Button onClick={calculateForce} className="flex-1">
                <Calculator className="mr-2 h-4 w-4" />
                Calculate
              </Button>
              <Button onClick={reset} variant="outline">
                Reset
              </Button>
            </div>

            {result !== null && (
              <>
                <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                  <CardHeader>
                    <CardTitle className="text-xl">Result</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold text-primary">
                      {result.toExponential(3)} N
                    </p>
                    <p className="text-sm text-muted-foreground mt-2">
                      Magnetic Force (Lorentz Force)
                    </p>
                  </CardContent>
                </Card>
                
                <CalculatorSuperCalcCTA config={calculatorCTAs["magnetic-force"]} trigger={result} />
              </>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-lg">Key Concepts</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm text-muted-foreground">
                <p>• Force is perpendicular to both v and B</p>
                <p>• Use right-hand rule for direction</p>
                <p>• Maximum force when v ⊥ B (θ = 90°)</p>
                <p>• No force when v ∥ B (θ = 0° or 180°)</p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
